import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import i18n from 'i18n-js';
import { buildUrl } from 'utils';
import Loading from 'components/Loading';
import Paginator from 'components/Paginator';
import { useHistory } from 'react-router-dom';
import { Card, BoxContainer } from 'components/UI';
import useStyles from './VersionsListWidget.styled';
import { DocumentsActions } from 'state/modules/documents';
import { Box, Button, Typography } from '@material-ui/core';
import VersionStatus from 'components/Documents/VersionStatus';
import StarOutlinedIcon from '@material-ui/icons/StarOutlined';

const VersionElement = ({ version, onClick }) => {
  const classes = useStyles();
  const file = version?.files?.length ? version.files[0] : undefined;
  return (
    <BoxContainer
      flex="1"
      display="flex"
      flexDirection="row"
      alignItems="flex-start"
      justifyContent="space-between"
      className={classes.versionElement}
      onClick={() => onClick(version.id)}
    >
      <Box className={classes.versionInfo}>
        <Box display="flex" alignItems="center" mb={1}>
          <Typography className={classes.versionTitle} variant="subtitle2">
            {version.version}
          </Typography>
          {version?.default && (
            <Box display="flex" flexDirection="row" alignItems="center">
              <StarOutlinedIcon className={classes.defaultVersionIcon} />
              <Typography variant="subtitle2" className={classes.defaultVersionText}>
                {i18n.t('documents.default_version')}
              </Typography>
            </Box>
          )}
        </Box>
        {file ? (
          <Typography className={classes.versionLabel} noWrap={true} variant="subtitle2">
            {`${i18n.t('documents.file')}: `}
            <span className={classes.versionName}>{file?.filename || '--'}</span>
          </Typography>
        ) : (
          <span className={classes.versionName}>{i18n.t('documents.no_file_attached')}</span>
        )}
      </Box>
      {version?.status !== 'NONE' && <VersionStatus status={version?.status} />}
    </BoxContainer>
  );
};

const VersionsListWidget = ({ documentId, menuItems = [] }) => {
  const classes = useStyles();
  const history = useHistory();
  const dispatch = useDispatch();
  const [params, setParams] = useState({
    page: 1,
    limit: 3,
  });

  const {
    fetchingVersions,
    versions: { list, pagination },
  } = useSelector((state) => state.documents);

  useEffect(() => {
    dispatch(DocumentsActions.fetchVersionsByDocumentId(documentId, params));
  }, [params, documentId, dispatch]);

  /**
   * Handle page change
   * @param {object} event
   * @param {number} newPage New page selected
   */
  const handleChangePage = (event, newPage) => {
    const newParams = { ...params, page: newPage + 1 };
    setParams(newParams);
  };

  /**
   * Handle version click
   * @param {string} versionId Id of the version
   */
  const handleVersionClick = (versionId) => {
    history.push(buildUrl(`/documents/${documentId}/versions/${versionId}`));
  };

  /**
   * Render title of the widget
   */
  const title = (
    <Box display="flex" alignItems="center" justifyContent="space-between">
      <Typography color="textPrimary" component="span">
        <Box fontSize={14} fontWeight="fontWeightMedium" data-cy="versions-title">
          {i18n.t('documents.versions')}
        </Box>
      </Typography>
      <Button
        type="button"
        variant="text"
        color="primary"
        disableFocusRipple={true}
        className={classes.button}
        data-cy="view-all-versions"
        onClick={() => history.push(buildUrl(`/documents/${documentId}/versions`))}
      >
        {i18n.t('common.view_all')}
      </Button>
    </Box>
  );

  return (
    <Card title={title} menuItems={menuItems} data-cy="versions-card" headerActionType="vertical">
      <Box className={classes.container} data-cy="versions-list">
        {fetchingVersions ? (
          <Box
            className={classes.loadingContainer}
            justifyContent="center"
            alignItems="center"
            minHeight={348}
            display="flex"
          >
            <Loading />
          </Box>
        ) : (
          <>
            {list?.length > 0 ? (
              <>
                <Box minHeight={295} data-cy="versions-items">
                  {list?.map((version) => (
                    <VersionElement
                      key={version.id}
                      version={version}
                      onClick={handleVersionClick}
                    />
                  ))}
                </Box>
                <Paginator
                  component="div"
                  limit={params?.limit}
                  pagination={pagination}
                  rowsPerPageOptions={[3]}
                  onPageChange={handleChangePage}
                />
              </>
            ) : (
              <Typography>
                <Box fontSize={13} component="span" className={classes.emptyPlaceholder}>
                  {i18n.t(`documents.empty_versions`)}
                </Box>
              </Typography>
            )}
          </>
        )}
      </Box>
    </Card>
  );
};

VersionsListWidget.propTypes = {
  /**
   * Id of the document
   */
  documentId: PropTypes.string.isRequired,
};

export default VersionsListWidget;
